/**
 * Player Store
 *
 * 오디오 플레이어 상태 관리
 * 기존 PlayerContext를 대체합니다.
 *
 * 주요 개선사항:
 * - 선택적 구독으로 리렌더링 최적화 (currentTime 변경 시 불필요한 리렌더링 방지)
 * - immer로 불변성 관리 간소화
 * - 볼륨 설정 자동 영속화
 */

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { TrackVersion } from '@/lib/api/orval/models';

// ============================================================================
// Types
// ============================================================================

export interface QueueItem {
  id: string;
  trackTitle: string;
  version: TrackVersion;
}

export type RepeatMode = 'off' | 'one' | 'all';

interface PlayerState {
  // 현재 재생 정보
  currentTrack: QueueItem | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;

  // 볼륨
  volume: number;
  isMuted: boolean;

  // 반복/셔플
  repeatMode: RepeatMode;
  isShuffled: boolean;

  // 큐
  queue: QueueItem[];
  queueIndex: number;

  // UI 상태
  isMinimized: boolean;
  isVisible: boolean;
}

interface PlayerActions {
  // 재생 제어
  playTrack: (item: QueueItem, queue?: QueueItem[]) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;

  // 볼륨 제어
  setVolume: (volume: number) => void;
  toggleMute: () => void;

  // 반복/셔플
  setRepeatMode: (mode: RepeatMode) => void;
  cycleRepeatMode: () => void;
  toggleShuffle: () => void;

  // 큐 관리
  addToQueue: (item: QueueItem) => void;
  addMultipleToQueue: (items: QueueItem[]) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  playFromQueue: (index: number) => void;
  nextTrack: () => void;
  prevTrack: () => void;

  // UI
  toggleMinimize: () => void;
  closePlayer: () => void;
  showPlayer: () => void;

  // 리셋
  reset: () => void;
}

type PlayerStore = PlayerState & PlayerActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: PlayerState = {
  currentTrack: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 0.7,
  isMuted: false,
  repeatMode: 'off',
  isShuffled: false,
  queue: [],
  queueIndex: -1,
  isMinimized: false,
  isVisible: false,
};

// ============================================================================
// Store
// ============================================================================

export const usePlayerStore = create<PlayerStore>()(
  subscribeWithSelector(
    persist(
      immer((set, get) => ({
        ...initialState,

        // ======================================================================
        // 재생 제어
        // ======================================================================

        playTrack: (item, queue) =>
          set((state) => {
            state.currentTrack = item;
            state.isPlaying = true;
            state.currentTime = 0;
            state.duration = 0;
            state.isVisible = true;

            // 큐가 제공되면 큐 설정 및 인덱스 계산
            if (queue && queue.length > 0) {
              state.queue = queue;
              const index = queue.findIndex((q) => q.id === item.id);
              state.queueIndex = index >= 0 ? index : 0;
            }
          }),

        play: () => set({ isPlaying: true }),

        pause: () => set({ isPlaying: false }),

        togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),

        setCurrentTime: (time) => set({ currentTime: time }),

        setDuration: (duration) => set({ duration }),

        // ======================================================================
        // 볼륨 제어
        // ======================================================================

        setVolume: (volume) =>
          set((state) => {
            const clampedVolume = Math.max(0, Math.min(1, volume));
            state.volume = clampedVolume;
            state.isMuted = clampedVolume === 0;
          }),

        toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),

        // ======================================================================
        // 반복/셔플
        // ======================================================================

        setRepeatMode: (mode) => set({ repeatMode: mode }),

        cycleRepeatMode: () =>
          set((state) => {
            const modes: RepeatMode[] = ['off', 'all', 'one'];
            const currentIndex = modes.indexOf(state.repeatMode);
            return { repeatMode: modes[(currentIndex + 1) % modes.length] };
          }),

        toggleShuffle: () => set((state) => ({ isShuffled: !state.isShuffled })),

        // ======================================================================
        // 큐 관리
        // ======================================================================

        addToQueue: (item) =>
          set((state) => {
            state.queue.push(item);
          }),

        addMultipleToQueue: (items) =>
          set((state) => {
            state.queue.push(...items);
          }),

        removeFromQueue: (index) =>
          set((state) => {
            state.queue.splice(index, 1);
            if (index < state.queueIndex) {
              state.queueIndex -= 1;
            } else if (index === state.queueIndex) {
              state.queueIndex = Math.min(state.queueIndex, state.queue.length - 1);
            }
          }),

        clearQueue: () => set({ queue: [], queueIndex: -1 }),

        playFromQueue: (index) =>
          set((state) => {
            if (index >= 0 && index < state.queue.length) {
              state.currentTrack = state.queue[index];
              state.queueIndex = index;
              state.isPlaying = true;
              state.currentTime = 0;
              state.isVisible = true;
            }
          }),

        nextTrack: () => {
          const state = get();

          if (state.queue.length === 0) {
            if (state.repeatMode === 'one') {
              set({ currentTime: 0 });
            } else {
              set({ isPlaying: false });
            }
            return;
          }

          let nextIndex = state.queueIndex + 1;

          if (state.isShuffled) {
            nextIndex = Math.floor(Math.random() * state.queue.length);
          }

          if (nextIndex >= state.queue.length) {
            if (state.repeatMode === 'all') {
              nextIndex = 0;
            } else {
              set({ isPlaying: false });
              return;
            }
          }

          set({
            currentTrack: state.queue[nextIndex],
            queueIndex: nextIndex,
            currentTime: 0,
            isPlaying: true,
          });
        },

        prevTrack: () => {
          const state = get();

          // 3초 이상 재생됐으면 처음으로
          if (state.currentTime > 3) {
            set({ currentTime: 0 });
            return;
          }

          if (state.queue.length === 0) {
            set({ currentTime: 0 });
            return;
          }

          let prevIndex = state.queueIndex - 1;
          if (prevIndex < 0) {
            if (state.repeatMode === 'all') {
              prevIndex = state.queue.length - 1;
            } else {
              set({ currentTime: 0 });
              return;
            }
          }

          set({
            currentTrack: state.queue[prevIndex],
            queueIndex: prevIndex,
            currentTime: 0,
            isPlaying: true,
          });
        },

        // ======================================================================
        // UI
        // ======================================================================

        toggleMinimize: () => set((state) => ({ isMinimized: !state.isMinimized })),

        closePlayer: () =>
          set({
            isVisible: false,
            isPlaying: false,
            currentTrack: null,
          }),

        showPlayer: () => set({ isVisible: true }),

        // ======================================================================
        // 리셋
        // ======================================================================

        reset: () => set(initialState),
      })),
      {
        name: 'player-storage',
        // 볼륨 관련 상태만 영속화
        partialize: (state) => ({
          volume: state.volume,
          isMuted: state.isMuted,
        }),
      }
    )
  )
);

// ============================================================================
// Selectors (선택적 구독을 위한 셀렉터)
// ============================================================================

// 기본 셀렉터
export const selectIsPlaying = (state: PlayerStore) => state.isPlaying;
export const selectCurrentTrack = (state: PlayerStore) => state.currentTrack;
export const selectCurrentTime = (state: PlayerStore) => state.currentTime;
export const selectDuration = (state: PlayerStore) => state.duration;
export const selectVolume = (state: PlayerStore) => state.volume;
export const selectIsMuted = (state: PlayerStore) => state.isMuted;
export const selectRepeatMode = (state: PlayerStore) => state.repeatMode;
export const selectIsShuffled = (state: PlayerStore) => state.isShuffled;
export const selectQueue = (state: PlayerStore) => state.queue;
export const selectQueueIndex = (state: PlayerStore) => state.queueIndex;
export const selectIsMinimized = (state: PlayerStore) => state.isMinimized;
export const selectIsVisible = (state: PlayerStore) => state.isVisible;

// 복합 셀렉터 (관련 상태 그룹)
export const selectVolumeState = (state: PlayerStore) => ({
  volume: state.volume,
  isMuted: state.isMuted,
});

export const selectProgress = (state: PlayerStore) => ({
  currentTime: state.currentTime,
  duration: state.duration,
});

export const selectQueueState = (state: PlayerStore) => ({
  queue: state.queue,
  queueIndex: state.queueIndex,
});

export const selectPlayerUI = (state: PlayerStore) => ({
  isMinimized: state.isMinimized,
  isVisible: state.isVisible,
});

export const selectRepeatShuffle = (state: PlayerStore) => ({
  repeatMode: state.repeatMode,
  isShuffled: state.isShuffled,
});

// ============================================================================
// Hook (기존 usePlayer 호환)
// ============================================================================

/**
 * usePlayer 훅
 *
 * 기존 PlayerContext의 usePlayer와 유사한 API를 제공합니다.
 * 단, audioRef는 별도의 AudioRefContext에서 관리합니다.
 *
 * @example
 * const { state, playTrack, togglePlay } = usePlayer();
 */
export function usePlayer() {
  const store = usePlayerStore();

  return {
    state: {
      currentTrack: store.currentTrack,
      isPlaying: store.isPlaying,
      currentTime: store.currentTime,
      duration: store.duration,
      volume: store.volume,
      isMuted: store.isMuted,
      repeatMode: store.repeatMode,
      isShuffled: store.isShuffled,
      queue: store.queue,
      queueIndex: store.queueIndex,
      isMinimized: store.isMinimized,
      isVisible: store.isVisible,
    },
    // 재생 컨트롤
    playTrack: store.playTrack,
    play: store.play,
    pause: store.pause,
    togglePlay: store.togglePlay,
    // 볼륨 컨트롤
    setVolume: store.setVolume,
    toggleMute: store.toggleMute,
    // 반복/셔플
    setRepeatMode: store.setRepeatMode,
    cycleRepeatMode: store.cycleRepeatMode,
    toggleShuffle: store.toggleShuffle,
    // 큐 관리
    addToQueue: store.addToQueue,
    addMultipleToQueue: store.addMultipleToQueue,
    removeFromQueue: store.removeFromQueue,
    clearQueue: store.clearQueue,
    playFromQueue: store.playFromQueue,
    nextTrack: store.nextTrack,
    prevTrack: store.prevTrack,
    // UI
    toggleMinimize: store.toggleMinimize,
    closePlayer: store.closePlayer,
  };
}

/**
 * usePlayerOptional 훅
 *
 * Provider 없이도 사용 가능한 버전 (항상 정의됨)
 */
export function usePlayerOptional() {
  return usePlayer();
}
